import { J2UploadToCloud } from '@e3dc-react/icons'
import { Upload } from '@e3dc-react/shell/Elements/FileTransfer'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Stack, Typography } from '@mui/material'
import { useField, useFormikContext } from 'formik'
import React, { useMemo } from 'react'
import { makeStyles } from 'tss-react/mui'
import { z } from 'zod'
import { DownloadFileItem, UpdateFileFormValues } from '../../models'
import { useBaseFileSchema } from '../../hooks'
import { splitFileNameAndExtenstion } from 'src/Libs/downloads'

const useStyles = makeStyles()(theme => ({
  dropContext: {
    color: theme.palette.primary.main,
  },
  dropText: {
    color: theme.palette.grey.grey03,
  },
  icon: {
    fontSize: '3rem',
    marginBottom: theme.spacing(-2),
  },
  uploadBox: {
    padding: theme.spacing(4),
  },
}))

interface Props {
  item: DownloadFileItem
  setUploadedFile: React.Dispatch<React.SetStateAction<File | undefined>>
}

type UploadFieldProps = {
  content: React.ReactElement
  onFilesDropped: (file: File | undefined) => void
  oneFile?: boolean
  fileValidationSchema?: z.ZodType<File>
  error?: string
}

/**
 * Form for replacing files
 */
const ReplaceFileForm: React.FC<Props> = ({ item, setUploadedFile }) => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const { setFieldValue } = useFormikContext<UpdateFileFormValues>()
  const [, meta] = useField<File | null>('file')
  const baseFileSchema = useBaseFileSchema()

  const hasErrorAfterTouch = useMemo<boolean>(() => {
    return Boolean(meta.error && meta.touched)
  }, [meta])

  const fileSchema: z.ZodType<File> = baseFileSchema.refine(
    file => {
      const { extension } = splitFileNameAndExtenstion(file.name)
      return extension
    },
    {
      message: t('documents.upload.wrong-file-type-error'),
    }
  )

  const onFileDropped = (file: File | undefined): void => {
    if (file) {
      setFieldValue('file', file)
    }
    setUploadedFile(file)
  }

  const uploadError = hasErrorAfterTouch && typeof meta.error === 'string' ? meta.error : undefined
  const UploadField = Upload as React.ComponentType<UploadFieldProps>

  return (
    <Stack direction={'column'} spacing={3} sx={{ mt: 2 }}>
      <UploadField
        content={
          <Box className={classes.uploadBox}>
            <J2UploadToCloud color="primary" className={classes.icon} />
            <Typography variant="h2" className={classes.dropContext}>
              {t('documents.upload.context-text')}
            </Typography>
            <Typography variant="body1" className={classes.dropText}>
              {t('documents.upload.drop-text')}
            </Typography>
          </Box>
        }
        onFilesDropped={onFileDropped}
        oneFile
        fileValidationSchema={fileSchema}
        error={uploadError}
      />
    </Stack>
  )
}

export default ReplaceFileForm
